import type { DashboardFeature } from '../../../services/dashboardService';

/** Pre–identity drafts (still read/migrated) */
export const LEGACY_DRAFT_STORAGE_KEY = 'glyyd:coach-dashboard-builder-draft';

export type BuilderDraftPayload = {
  dashboardName: string;
  features: DashboardFeature[];
  editingDashboardId: string | null;
  savedAt: number;
};

export type DashboardBuilderDraftSummary = {
  editingDashboardId: string | null;
  dashboardName: string;
  savedAt: number;
  widgetCount: number;
};

/** v3: coach + builder slot only (not active team — dashboards are coach-scoped). */
export function buildScopedDraftStorageKey(
  userId: string,
  editingDashboardId: string | null
): string {
  const slot = editingDashboardId == null ? 'new' : `edit:${editingDashboardId}`;
  return `${LEGACY_DRAFT_STORAGE_KEY}:v3:${userId}:${slot}`;
}

export function draftMatchesBuilderSlot(
  parsed: BuilderDraftPayload,
  editingDashboardId: string | null
): boolean {
  if (editingDashboardId) {
    return parsed.editingDashboardId === editingDashboardId;
  }
  return parsed.editingDashboardId == null;
}

export function parseDraftPayloadRaw(raw: string): BuilderDraftPayload | null {
  try {
    const parsed = JSON.parse(raw) as BuilderDraftPayload;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.dashboardName !== 'string') return null;
    if (!Array.isArray(parsed.features) || parsed.features.length === 0) return null;
    if (!('savedAt' in parsed) || typeof parsed.savedAt !== 'number') return null;
    if (!('editingDashboardId' in parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export type DraftReadOptions = {
  /** Oldest unscoped key — migrate only if payload matches `slotEditingDashboardId` */
  migrateFromLegacyKey?: string;
  /** v2 keys were `...:v2:${userId}:${teamId}` — scan and merge into v3 once */
  migrateFromV2TeamScopedKeys?: { userId: string };
  /** Required when using legacy or v2 migration so we do not promote a draft for the wrong builder */
  slotEditingDashboardId?: string | null;
};

function migrateV2TeamScopedDraftsToKey(
  targetKey: string,
  userId: string,
  slotEditingDashboardId: string | null
): string | null {
  const prefix = `${LEGACY_DRAFT_STORAGE_KEY}:v2:${userId}:`;
  const matchingKeys: string[] = [];
  let best: BuilderDraftPayload | null = null;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const candidateRaw = localStorage.getItem(key);
      if (!candidateRaw) continue;
      const parsed = parseDraftPayloadRaw(candidateRaw);
      if (parsed && draftMatchesBuilderSlot(parsed, slotEditingDashboardId)) {
        matchingKeys.push(key);
        if (!best || parsed.savedAt > best.savedAt) {
          best = parsed;
        }
      }
    }

    if (!best || matchingKeys.length === 0) return null;

    const serialized = JSON.stringify(best);
    localStorage.setItem(targetKey, serialized);
    for (const k of matchingKeys) {
      localStorage.removeItem(k);
    }
    return serialized;
  } catch {
    return null;
  }
}

export function readDraftFromBrowserStorage(
  storageKey: string,
  options?: DraftReadOptions
): BuilderDraftPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    let raw = localStorage.getItem(storageKey);

    const slotId = options?.slotEditingDashboardId;
    const hasSlotFilter = slotId !== undefined;

    if (
      !raw &&
      options?.migrateFromV2TeamScopedKeys &&
      hasSlotFilter &&
      options.migrateFromV2TeamScopedKeys.userId
    ) {
      const migrated = migrateV2TeamScopedDraftsToKey(
        storageKey,
        options.migrateFromV2TeamScopedKeys.userId,
        slotId ?? null
      );
      if (migrated) {
        raw = localStorage.getItem(storageKey) ?? migrated;
      }
    }

    const legacyKey = options?.migrateFromLegacyKey;
    if (!raw && legacyKey && legacyKey !== storageKey && hasSlotFilter) {
      const legacyRaw = localStorage.getItem(legacyKey);
      if (legacyRaw) {
        const parsedLegacy = parseDraftPayloadRaw(legacyRaw);
        if (parsedLegacy && draftMatchesBuilderSlot(parsedLegacy, slotId ?? null)) {
          try {
            if (!localStorage.getItem(storageKey)) {
              localStorage.setItem(storageKey, legacyRaw);
            }
            localStorage.removeItem(legacyKey);
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem(legacyKey);
            }
          } catch {
            /* if scoped write fails, fall through and parse legacyRaw */
          }
          raw = localStorage.getItem(storageKey) ?? legacyRaw;
        }
      }
    } else if (!raw && legacyKey && legacyKey !== storageKey && !hasSlotFilter) {
      const legacyRaw = localStorage.getItem(legacyKey);
      if (legacyRaw) {
        try {
          if (!localStorage.getItem(storageKey)) {
            localStorage.setItem(storageKey, legacyRaw);
          }
          localStorage.removeItem(legacyKey);
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(legacyKey);
          }
        } catch {
          /* if scoped write fails, fall through and parse legacyRaw */
        }
        raw = localStorage.getItem(storageKey) ?? legacyRaw;
      }
    }

    if (!raw && typeof sessionStorage !== 'undefined') {
      raw = sessionStorage.getItem(storageKey) ?? null;
      if (raw) {
        try {
          localStorage.setItem(storageKey, raw);
          sessionStorage.removeItem(storageKey);
        } catch {
          /* keep draft in sessionStorage if localStorage fails */
        }
      }
    }
    if (!raw) return null;
    return parseDraftPayloadRaw(raw);
  } catch {
    return null;
  }
}

export function writeDraftToStorage(storageKey: string, payload: BuilderDraftPayload): boolean {
  try {
    localStorage.setItem(storageKey, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function clearDraftFromStorage(storageKey: string) {
  try {
    localStorage.removeItem(storageKey);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(storageKey);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function draftSlotKey(editingDashboardId: string | null): string {
  return editingDashboardId ?? '__new__';
}

function payloadToSummary(parsed: BuilderDraftPayload): DashboardBuilderDraftSummary {
  return {
    editingDashboardId: parsed.editingDashboardId,
    dashboardName: parsed.dashboardName.trim() || 'Untitled Dashboard',
    savedAt: parsed.savedAt,
    widgetCount: parsed.features.length,
  };
}

/** All local drafts for the signed-in coach (v3 keys + unmigrated legacy/v2). */
export function listDashboardBuilderDrafts(userId: string): DashboardBuilderDraftSummary[] {
  if (typeof window === 'undefined') return [];

  const bySlot = new Map<string, DashboardBuilderDraftSummary>();
  const v3Prefix = `${LEGACY_DRAFT_STORAGE_KEY}:v3:${userId}:`;
  const v2Prefix = `${LEGACY_DRAFT_STORAGE_KEY}:v2:${userId}:`;

  const consider = (parsed: BuilderDraftPayload | null) => {
    if (!parsed) return;
    const slot = draftSlotKey(parsed.editingDashboardId);
    const summary = payloadToSummary(parsed);
    const existing = bySlot.get(slot);
    if (!existing || summary.savedAt > existing.savedAt) {
      bySlot.set(slot, summary);
    }
  };

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith(v3Prefix) || key.startsWith(v2Prefix)) {
        consider(parseDraftPayloadRaw(localStorage.getItem(key) ?? ''));
      }
    }

    const legacyRaw = localStorage.getItem(LEGACY_DRAFT_STORAGE_KEY);
    if (legacyRaw) {
      consider(parseDraftPayloadRaw(legacyRaw));
    }
  } catch {
    return [];
  }

  return Array.from(bySlot.values()).sort((a, b) => b.savedAt - a.savedAt);
}

/** Sidebar / navigation target for opening a draft in the builder. */
export function draftNavigationSubItem(editingDashboardId: string | null): string {
  return editingDashboardId == null ? 'draft-new' : `draft-edit:${editingDashboardId}`;
}

export type BuilderDraftContent = Pick<
  BuilderDraftPayload,
  'dashboardName' | 'features' | 'editingDashboardId'
>;

function featureLayoutSignature(feature: DashboardFeature): string {
  return JSON.stringify({
    featureId: feature.featureId,
    displayOrder: feature.displayOrder,
    gridX: feature.gridX,
    gridY: feature.gridY,
    gridW: feature.gridW,
    gridH: feature.gridH,
    featureConfig: feature.featureConfig ?? {},
  });
}

/** True when name, slot, and widget layout match (ignores savedAt). */
export function builderDraftsContentEqual(a: BuilderDraftContent, b: BuilderDraftContent): boolean {
  if (a.dashboardName !== b.dashboardName) return false;
  if (a.editingDashboardId !== b.editingDashboardId) return false;
  if (a.features.length !== b.features.length) return false;
  const sigsA = a.features.map(featureLayoutSignature).sort();
  const sigsB = b.features.map(featureLayoutSignature).sort();
  return sigsA.every((sig, index) => sig === sigsB[index]);
}
